import {entry, fillArray, noop, objectFromEntries} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import produce from 'immer';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureEdgeType,
  ProcedureParameterConfirmationMethod,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SimpleTableEditable, standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {useSimpleUI} from 'notebook/simple-tables/useSimpleEvaluation';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toColumnConstraint from 'notebook/structured-tables/columns/toColumnConstraint';
import {toSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/toSchemaFieldColumnDefinition';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import {countBlockingValidationErrors} from 'notebook/structured-tables/util/hasBlockingValidationErrors';
import kindToIcon from 'notebook/structured-tables/util/kindToIcon';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {EvaluatedTable, FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {
  Cell,
  Cells,
  ColumnConstraint,
  CustomEvalResult,
  RowData,
  Table,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {areTypedValuesEquivalent, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn, ResolveTypedValueToTypeFn} from 'notebook/tables/typed-values/TypedValueResolution';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getConfirmationMethodStructuredTableColumn,
  getConfirmationMethodValueFromCell,
  makeConfirmationMethodCell,
} from 'procedures/tables/procedureTableAdapterUtils';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ExperimentFlowchart, ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition, Condition, isPersistedCondition} from 'studies/typed/shared/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import {NEAR_BLACK} from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

import {
  getDefaultLabelByComponentNameAndProcedureType,
  getEquipmentModelsLabelByProcedureType,
  TABLE_NAME_BY_COMPONENT_NAME,
} from './ProcedureExperimentConstants';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
  ExperimentalInputsPerMethod,
  ExperimentalInputsValuesPerCondition,
  ParameterFields,
  ParameterFieldValues,
} from './ProcedurePlanTypes';
import {
  validateEquipment,
  validateMaterialInputConfirmationMethod,
  validateNumericFieldValue,
  validateValueRange,
  validateArchivedEntity,
  validateNumberOfValues,
} from './validateInputTable';
import VaryingParameterName from './VaryingParameterName';

const CELL_ALIGNMENT = 'left';
// Experimental input tables are rendered via `SimpleTableEditable` (not the procedure config structured
// table stack), but we intentionally derive icons from matching `StructuredTableKind` values so header
// icons stay in sync with method editor definition tables.
const INPUT_TABLE_KIND_BY_COMPONENT_NAME: Record<ExperimentalInputsComponentName, StructuredTableKind> = {
  [ExperimentalInputsComponentName.PARAMETERS]: StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG,
  [ExperimentalInputsComponentName.MATERIAL_INPUTS]: StructuredTableKind.PROCEDURE_MATERIAL_INPUTS,
  [ExperimentalInputsComponentName.EQUIPMENT]: StructuredTableKind.PROCEDURE_EQUIPMENT,
};

/**
 * A hook for managing the state needed for any Parameter+ InputTable.
 * @returns a SimpleTableEditable JSX.Element
 */
export function useInputTable(
  componentName: ExperimentalInputsComponentName,
  {
    context,
    selectedMethod,
  }: {
    context: ExperimentalInputsContextProperties;
    selectedMethod: ExperimentMethod;
    lockedReasonByInputId?: Record<string, string> | null;
    procedureType: ProcedureType;
  }
): JSX.Element | null {
  const experimentalInputs = context.draftInputs[selectedMethod.id];
  const tableName = getTableName(selectedMethod.id, componentName);
  const enhancedProps = useSimpleUI(tableName, context.simpleTableDoc, true);
  if (enhancedProps == null) {
    return null;
  }
  return (
    <SimpleTableEditable
      {...standaloneProps}
      {...enhancedProps}
      dataTestAttrs={{component: 'ExperimentalInputTable', key: componentName}}
      hideFormulaBox={true}
      isCompactBottomSpacing={true}
      isCollapsible={true}
      isLeftAlignRowHeader={true}
      icon={getInputTableHeaderIcon(componentName)}
      locked={context.locked}
      cid={`${context.simpleTableDoc.cid}-${tableName}`}
      updateHandler={(index: number, {table}: {table: Table}) => {
        const {newExperimentalInputs, newConfirmationMethods} = convertTableToExperimentalInputs(
          experimentalInputs.values,
          experimentalInputs.fields[componentName],
          experimentalInputs.defaultValues[componentName],
          table.table,
          table.columnLabels,
          componentName,
          experimentalInputs.parameterConfirmationMethods
        );
        context.setExperimentalInputs(selectedMethod.id, newExperimentalInputs, newConfirmationMethods);
      }}
      storeState={noop}
      restoreState={noop}
      canRename={false}
    />
  );
}

function getTableName(methodId: string, componentName: ExperimentalInputsComponentName): string {
  return `${methodId}_${componentName}`;
}

function getInputTableHeaderIcon(componentName: ExperimentalInputsComponentName): BenchlingIconName {
  return kindToIcon(INPUT_TABLE_KIND_BY_COMPONENT_NAME[componentName]);
}

function getHasFieldColumn(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): boolean {
  return experimentalInputs.fields[componentName].some((fields) => fields.length > 1);
}

function getHasConfirmationMethodColumn(componentName: ExperimentalInputsComponentName): boolean {
  return (
    BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES &&
    [ExperimentalInputsComponentName.PARAMETERS, ExperimentalInputsComponentName.MATERIAL_INPUTS].includes(
      componentName
    )
  );
}

function getHasEquipmentModelsColumn(componentName: ExperimentalInputsComponentName): boolean {
  return componentName === ExperimentalInputsComponentName.EQUIPMENT;
}

export function getNumNonConditionColumns(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): number {
  const numAlwaysPresentColumns = 2; // Name and Setpoint/Default
  return (
    numAlwaysPresentColumns +
    (getHasFieldColumn(experimentalInputs, componentName) ? 1 : 0) +
    (getHasConfirmationMethodColumn(componentName) ? 1 : 0) +
    (getHasEquipmentModelsColumn(componentName) ? 1 : 0)
  );
}

/**
 * Returns a mapping from parameter+ id to the reason why the value is locked.
 *
 * @package exported for testing
 */
export function getLockedMaterialInputReasons(
  method: ExperimentMethod,
  procedureFlowchart: ExperimentFlowchart,
  procedureType: ProcedureType
): {[cid: string]: string} {
  const inboundMaterialIds = new Set(
    procedureFlowchart.edges
      .filter((edge) => edge.type === ProcedureEdgeType.MATERIAL_EDGE)
      .map((edge) => edge.targetId)
  );

  const materialIds = method.materialInputSettings.map((material) => material.version.id);

  return objectFromEntries(
    materialIds
      .filter((materialId) => inboundMaterialIds.has(materialId))
      .map((materialId) => [
        materialId,
        `This material is mapped from another ${getProcedureUIString(
          ProceduresStringKeys.METHOD,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} and cannot be varied in the experiment.`,
      ])
  );
}

function getVaryingParameterIndices(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): Array<number> {
  if (experimentalInputs.values.length === 0) {
    return [];
  }

  return experimentalInputs.fields[componentName].reduce<Array<number>>((varyingIndices, fields, index) => {
    if (getIsVaryingParameter(experimentalInputs, componentName, index, fields)) {
      varyingIndices.push(index);
    }
    return varyingIndices;
  }, []);
}

function getIsVaryingParameter(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  parameterIndex: number,
  fields: ParameterFields[number]
): boolean {
  return experimentalInputs.values.some((conditionValuesByComponent) => {
    const conditionValues = conditionValuesByComponent[componentName][parameterIndex];
    const defaultValues = experimentalInputs.defaultValues[componentName][parameterIndex];
    return fields.some(
      (_, fieldIndex) => !areTypedValuesEquivalent(conditionValues[fieldIndex], defaultValues[fieldIndex])
    );
  });
}

export function convertExperimentalInputsToTableMap(
  experimentalInputs: ExperimentalInputs,
  flowchart: ExperimentFlowchart,
  procedureType: ProcedureType,
  isLocked: boolean,
  conditions?: Array<BaseCondition>,
  allowEditAllConditions: boolean = false,
  archivedEntityIds?: ReadonlySet<string>
): FullTableMap {
  return objectFromEntries(
    Object.entries(experimentalInputs).flatMap(([methodId, inputsPerMethod]) => {
      const method = flowchart.nodes.find((m) => m.id === methodId);
      assert(method != null, 'Expected method to be found', {methodId, nodes: flowchart.nodes});
      const lockedReasonByInputId = getLockedMaterialInputReasons(method, flowchart, procedureType);
      return Object.values(ExperimentalInputsComponentName).flatMap((componentName) => {
        const table = convertExperimentalInputsToTable(
          inputsPerMethod,
          componentName,
          lockedReasonByInputId,
          conditions ?? [],
          procedureType,
          method,
          isLocked,
          allowEditAllConditions,
          archivedEntityIds
        );
        return table ? [entry(getTableName(methodId, componentName), table)] : [];
      });
    })
  );
}

/**
 * @package exported for testing
 */
export function convertTableToExperimentalInputs(
  currentExperimentalInputs: ExperimentalInputsValuesPerCondition,
  parameterFields: ParameterFields,
  defaultValues: ParameterFieldValues,
  tableCells: Cells,
  columnLabels: Array<string | null>,
  componentName: ExperimentalInputsComponentName,
  currentConfirmationMethods: Record<
    ExperimentalInputsComponentName,
    Array<ProcedureParameterConfirmationMethod>
  >
): {
  newExperimentalInputs: ExperimentalInputsValuesPerCondition;
  newConfirmationMethods: Record<
    ExperimentalInputsComponentName,
    Array<ProcedureParameterConfirmationMethod>
  >;
} {
  const numConditions = currentExperimentalInputs.length;
  const newExperimentalInputs = produce(currentExperimentalInputs, (draftInputs) => {
    for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(parameterFields)) {
      for (const {colIndex, conditionIndex} of enumTableParamValueCols(numConditions, columnLabels.length)) {
        const cell = tableCells[rowIndex][colIndex];
        const value = cell.typedValue ?? {type: BaseTypedValueType.STRING, rawValue: cell.text};
        const defaultValue = defaultValues[parameterIndex][fieldIndex];
        // We re-hydrate the default value in place of any empty cell. This keeps the experimentalInputs free
        // from the notion that "empty implies default"
        draftInputs[conditionIndex][componentName][parameterIndex][fieldIndex] = !isTypedValueEmpty(value)
          ? value
          : defaultValue;
      }
    }
  });

  const confirmationMethodColumnIndex = columnLabels.findIndex(
    (label, i) =>
      label === 'Confirmation method' &&
      // Not a condition column
      i < columnLabels.length - numConditions
  );
  const newConfirmationMethods =
    confirmationMethodColumnIndex === -1
      ? currentConfirmationMethods // No column -> don't change confirmation methods
      : produce(currentConfirmationMethods, (draftConfirmationMethods) => {
          for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(parameterFields)) {
            // Only first fieldIndex because cells are merged across fields of the same param
            if (fieldIndex === 0) {
              const confirmationMethodFromTable = getConfirmationMethodValueFromCell(
                tableCells[rowIndex][confirmationMethodColumnIndex]
              );
              draftConfirmationMethods[componentName][parameterIndex] = confirmationMethodFromTable;
            }
          }
        });

  return {newExperimentalInputs, newConfirmationMethods};
}

export function convertOutputTableMapToEvaluatedValues(
  outputTableMap: OutputTableMap,
  experimentalInputs: ExperimentalInputs
): ExperimentalInputEvaluatedValues {
  return objectFromEntries(
    Object.entries(experimentalInputs).map(([methodId, methodInputs]) => {
      const numConditions = methodInputs.values.length;
      // First, seed our return method values to an empty list of values per condition/component.
      const values: ExperimentalInputsValuesPerCondition = fillArray(numConditions, () => ({
        [ExperimentalInputsComponentName.EQUIPMENT]: [],
        [ExperimentalInputsComponentName.MATERIAL_INPUTS]: [],
        [ExperimentalInputsComponentName.PARAMETERS]: [],
      }));

      // Then, enumerate the cells in the evaluated tables, and fill them into our values array.
      let blockingValueErrorCount = 0;
      Object.values(ExperimentalInputsComponentName).forEach((componentName) => {
        const tableName = getTableName(methodId, componentName);
        const outputTable: EvaluatedTable | null = outputTableMap[tableName];
        if (outputTable == null) {
          // See early return in convertExperimentalInputsToTable.
          assert(
            experimentalInputs[methodId].parameterNames[componentName].length === 0,
            'Expected no table only if there are no parameters'
          );
        } else {
          blockingValueErrorCount += countBlockingValidationErrors(outputTable);
          const fields = methodInputs.fields[componentName];
          for (const {rowIndex, parameterIndex, fieldIndex} of enumTableRows(fields)) {
            const numColumns = outputTable[0].length; // For loop body only hit when table has rows
            for (const {colIndex, conditionIndex} of enumTableParamValueCols(numConditions, numColumns)) {
              values[conditionIndex][componentName][parameterIndex] ??= [];
              values[conditionIndex][componentName][parameterIndex][fieldIndex] =
                outputTable[rowIndex][colIndex].value;
            }
          }
        }
      });
      return entry(methodId, {blockingValueErrorCount, values});
    })
  );
}

function* enumTableRows(
  parameterFields: ParameterFields
): Generator<{rowIndex: number; parameterIndex: number; fieldIndex: number}> {
  let rowIndex = 0;
  for (let parameterIndex = 0; parameterIndex < parameterFields.length; ++parameterIndex) {
    const numFields = parameterFields[parameterIndex].length;
    for (let fieldIndex = 0; fieldIndex < numFields; ++fieldIndex) {
      yield {rowIndex, parameterIndex, fieldIndex};
      rowIndex++;
    }
  }
}

function* enumTableParamValueCols(
  numConditions: number,
  numColumns: number
): Generator<{colIndex: number; conditionIndex: number}> {
  const numNonConditionColumns = numColumns - numConditions;
  for (let conditionIndex = 0; conditionIndex < numConditions; ++conditionIndex) {
    yield {colIndex: conditionIndex + numNonConditionColumns, conditionIndex};
  }
}

export function getDefaultConditions(length: number, procedureType: ProcedureType): Array<Condition> {
  return Array.from({length}, (_, conditionNumber: number) => ({
    numReplicates: 1,
    name: `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${
      conditionNumber + 1
    }`,
  }));
}

interface GetCellArgs {
  experimentalInputs: ExperimentalInputsPerMethod;
  componentName: ExperimentalInputsComponentName;
  parameterIndex: number;
  fieldIndex: number;
  lockedReasonByInputId: Record<string, string> | null;
  method: ExperimentMethod;
  isLockedPerCondition: Array<boolean>;
  shouldLockConfirmationMethod: boolean;
  isTableLocked: boolean;
  isVaryingParameter: boolean;
  archivedEntityIds?: ReadonlySet<string>;
}

interface ColumnHandler {
  label: string;
  structuredTableColumn: StructuredTableColumn;
  getCellForFieldRow: (args: GetCellArgs) => Cell;
}

function getColumnHandlers(
  conditions: Array<BaseCondition>,
  experimentalInputs: ExperimentalInputsPerMethod,
  {
    componentName,
    procedureType,
  }: {componentName: ExperimentalInputsComponentName; procedureType: ProcedureType}
): Array<ColumnHandler> {
  const defaultSetpointColumnLabel = getDefaultLabelByComponentNameAndProcedureType(
    componentName,
    procedureType
  );

  const nameColumn: ColumnHandler = {
    label: 'Name',
    structuredTableColumn: makeStructuredTableColumn({columnType: TableColumnType.STRING, name: 'Name'}),
    getCellForFieldRow: makeParamNameCell,
  };

  const maybeConfirmationMethodColumn: ColumnHandler | null = getHasConfirmationMethodColumn(componentName)
    ? {
        label: 'Confirmation method',
        structuredTableColumn: getConfirmationMethodStructuredTableColumn(),
        getCellForFieldRow: makeParamConfirmationMethodCell,
      }
    : null;

  const maybeFieldColumn: ColumnHandler | null = getHasFieldColumn(experimentalInputs, componentName)
    ? {
        label: 'Field',
        structuredTableColumn: makeStructuredTableColumn({columnType: TableColumnType.STRING, name: 'Field'}),
        getCellForFieldRow: makeParamFieldCell,
      }
    : null;

  const maybeEquipmentModelsColumn: ColumnHandler | null = getHasEquipmentModelsColumn(componentName)
    ? {
        label: getEquipmentModelsLabelByProcedureType(procedureType),
        structuredTableColumn: makeStructuredTableColumn({
          columnType: TableColumnType.ROW_DELEGATE,
          name: getEquipmentModelsLabelByProcedureType(procedureType),
        }),
        getCellForFieldRow: makeEquipmentModelsCell,
      }
    : null;

  const defaultSetpointColumn: ColumnHandler = {
    label: defaultSetpointColumnLabel,
    structuredTableColumn: makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: defaultSetpointColumnLabel,
    }),
    getCellForFieldRow: makeParamDefaultSetpointCell,
  };

  const conditionColumns: Array<ColumnHandler> = conditions.map((c, i) => ({
    label: c.name,
    structuredTableColumn: makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: c.name,
    }),
    getCellForFieldRow: (args) => makeConditionValueCell(args, i),
  }));

  return [
    nameColumn,
    maybeConfirmationMethodColumn,
    maybeFieldColumn,
    maybeEquipmentModelsColumn,
    defaultSetpointColumn,
    ...conditionColumns,
  ].filter(<T,>(c: T | null): c is T => c != null);
}

function makeParamNameCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
  isVaryingParameter,
}: GetCellArgs): Cell {
  const hasFieldColumn = getHasFieldColumn(experimentalInputs, componentName);
  const name = experimentalInputs.parameterNames[componentName][parameterIndex];
  const formattedValue =
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN && isVaryingParameter ? (
      <VaryingParameterName name={name} />
    ) : undefined;
  // Handle merge cells
  if (hasFieldColumn) {
    return fieldIndex === 0
      ? {
          text: name,
          mergeRowSpan: experimentalInputs.fields[componentName][parameterIndex].length,
          locked: true,
          formattedValue,
        }
      : {text: '', mergeRowSpan: 0, locked: true};
  } else {
    return {text: name, locked: true, formattedValue};
  }
}

function makeParamConfirmationMethodCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
  method,
  shouldLockConfirmationMethod,
}: GetCellArgs): Cell {
  const hasFieldColumn = getHasFieldColumn(experimentalInputs, componentName);
  const confirmationMethod = experimentalInputs.parameterConfirmationMethods[componentName][parameterIndex];
  const parameterId = experimentalInputs.ids[componentName][parameterIndex];
  const baseCell: Cell = {
    ...makeConfirmationMethodCell(confirmationMethod),
    locked: shouldLockConfirmationMethod,
    customEvalArgs: [],
    customEval: (
      typedValue: TypedValue,
      columnDefinition: ColumnDefinition,
      cellDependencies: Array<TypedValue | null>,
      customEvalOptions: unknown,
      resolveFromResolutionQuery: ResolveQueryFn,
      resolveTypedValueToType: ResolveTypedValueToTypeFn
    ) => {
      const evalResult: CustomEvalResult = {val: typedValue, error: null, locked: false};
      if (componentName !== ExperimentalInputsComponentName.MATERIAL_INPUTS) {
        return evalResult;
      }
      const dataType = method.materialInputSettings.find(({version}) => version.id === parameterId)?.version
        .dataType;
      if (dataType == null) {
        logging.info('Cannot find matching material input data type', {extra: {parameterId}});
      }
      validateMaterialInputConfirmationMethod(
        evalResult,
        typedValue,
        confirmationMethod,
        dataType,
        resolveFromResolutionQuery
      );
      return evalResult;
    },
  };

  // Handle merge cells
  if (hasFieldColumn) {
    return fieldIndex === 0
      ? {...baseCell, mergeRowSpan: experimentalInputs.fields[componentName][parameterIndex].length}
      : {...baseCell, mergeRowSpan: 0};
  } else {
    return baseCell;
  }
}

function makeParamFieldCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  return {
    text: experimentalInputs.fields[componentName][parameterIndex][fieldIndex].displayName,
    locked: true,
  };
}

function makeParamDefaultSetpointCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  const typedValue = experimentalInputs.defaultValues[componentName][parameterIndex][fieldIndex];
  return {
    text: '',
    typedValue,
    locked: true,
    align: CELL_ALIGNMENT,
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

function makeEquipmentModelsCell({
  experimentalInputs,
  componentName,
  parameterIndex,
  fieldIndex,
}: GetCellArgs): Cell {
  const equipmentParameterField = experimentalInputs.fields[componentName][parameterIndex][fieldIndex];
  return {
    text: '',
    typedValue: equipmentParameterField.allowedEquipmentModels ?? null,
    locked: true,
    align: CELL_ALIGNMENT,
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

function makeConditionValueCell(
  {
    experimentalInputs,
    componentName,
    parameterIndex,
    fieldIndex,
    lockedReasonByInputId,
    isLockedPerCondition,
    isTableLocked,
    archivedEntityIds,
  }: GetCellArgs,
  conditionIndex: number
): Cell {
  const value = experimentalInputs.values[conditionIndex][componentName][parameterIndex][fieldIndex];
  const lockedReason = lockedReasonByInputId?.[experimentalInputs.ids[componentName][parameterIndex]];

  const isDefault = areTypedValuesEquivalent(
    value,
    experimentalInputs.defaultValues[componentName][parameterIndex][fieldIndex]
  );
  const isColumnLocked = isLockedPerCondition[conditionIndex];
  return {
    text: '',
    typedValue: value,
    customEvalArgs: [],
    customEval: (
      typedValue: TypedValue,
      columnDefinition: ColumnDefinition,
      cellDependencies: Array<TypedValue | null>,
      customEvalOptions: unknown,
      resolveFromResolutionQuery: ResolveQueryFn,
      resolveTypedValueToType: ResolveTypedValueToTypeFn
    ) => {
      const evalResult: CustomEvalResult = {val: typedValue, error: null, locked: false};
      const field = experimentalInputs.fields[componentName][parameterIndex][fieldIndex];

      validateNumericFieldValue(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateValueRange(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateNumberOfValues(evalResult, typedValue, field, columnDefinition, resolveTypedValueToType);
      validateEquipment(evalResult, typedValue, field, resolveFromResolutionQuery);
      if (archivedEntityIds != null) {
        validateArchivedEntity(evalResult, typedValue, archivedEntityIds);
      }
      return evalResult;
    },
    locked: lockedReason != null || isColumnLocked,
    userAnnotationIndicator:
      lockedReason && !isTableLocked && !isColumnLocked ? (
        <ErrorTriangle
          state={TriangleState.VALID_WITH_TRIANGLE}
          error={{type: '', message: lockedReason}}
          location={TriangleLocation.CELL}
        />
      ) : undefined,
    align: CELL_ALIGNMENT,
    foregroundColor: isDefault ? Color.GRAY_500 : Color.GRAY_900,
    bold: !isDefault,
  };
}

/**
 * @returns a table that can be evaluated, `null` if there are no rows to render
 *
 * @package exported for testing
 */
export function convertExperimentalInputsToTable(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName,
  lockedReasonByInputId: Record<string, string> | null,
  conditions: Array<BaseCondition>,
  procedureType: ProcedureType,
  method: ExperimentMethod,
  isLocked: boolean,
  allowEditAllConditions: boolean = false,
  archivedEntityIds?: ReadonlySet<string>
): Table | null {
  const numConditions = experimentalInputs.values.length;
  const conditionsOrDefault =
    conditions.length === 0 ? getDefaultConditions(numConditions, procedureType) : conditions;
  const parameterNames = experimentalInputs.parameterNames[componentName];
  const numParameters = parameterNames.length;
  if (numParameters === 0) {
    return null;
  }
  const fields = experimentalInputs.fields[componentName];

  const rowLabels = parameterNames.map((_name: string, _index: number) => '');

  const columnHandlers = getColumnHandlers(conditionsOrDefault, experimentalInputs, {
    componentName,
    procedureType,
  });
  const columnLabels = columnHandlers.map(({label}) => label);
  const columnConstraints = columnHandlers.map(({structuredTableColumn}) =>
    toColumnConstraint({structuredTableColumn, schemas: []})
  );

  const varyingParameterIndices = BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN
    ? new Set(getVaryingParameterIndices(experimentalInputs, componentName))
    : new Set<number>();
  const rows: Array<Array<Cell>> = fillArray(numParameters, (parameterIndex) =>
    fillArray(
      fields[parameterIndex].length,
      // for each field (eg material and amount), return the cells in the row
      (fieldIndex) =>
        columnHandlers.map(({getCellForFieldRow}) =>
          getCellForFieldRow({
            experimentalInputs,
            componentName,
            parameterIndex,
            fieldIndex,
            lockedReasonByInputId,
            method,
            // For plans (allowEditAllConditions=true), all conditions are editable.
            // For runs, only new conditions (without id) are editable.
            isLockedPerCondition: allowEditAllConditions
              ? conditions.map(() => false)
              : conditions.map((c) => isPersistedCondition(c)),
            shouldLockConfirmationMethod: allowEditAllConditions
              ? false
              : conditions.some((c) => isPersistedCondition(c)),
            isTableLocked: isLocked,
            isVaryingParameter: varyingParameterIndices.has(parameterIndex),
            archivedEntityIds,
          })
        )
    )
  ).flatMap(
    // rows[parameter][field][condition] -> rows[parameterField][condition]
    (fieldRowsForValue) => fieldRowsForValue
  );

  const delegatedColumnConstraintByValueIndex: Array<Array<ColumnConstraint>> = getDelegatedColumnConstraints(
    experimentalInputs,
    componentName
  );

  const rowData: Array<RowData> = fillArray(numParameters, (parameterIndex) =>
    fillArray(fields[parameterIndex].length, (fieldIndex) => ({
      delegatedColumnConstraints: new Map(
        columnConstraints.map((col) => [
          col.structuredTableColumn!.provenanceUuid,
          delegatedColumnConstraintByValueIndex[parameterIndex][fieldIndex],
        ])
      ),
    }))
  ).flatMap((rowDataWithFieldIndex) => rowDataWithFieldIndex);

  return {
    name: TABLE_NAME_BY_COMPONENT_NAME[componentName],
    _firstColumnWidth: 0,
    widths: columnLabels.map(() => 120),
    rowLabels,
    columnLabels,
    columnConstraints,
    headerCol: false,
    headerRow: false,
    table: rows,
    lockedDims: true,
    allowColumnEdits: false,
    rowData,
  };
}

function getDelegatedColumnConstraints(
  experimentalInputs: ExperimentalInputsPerMethod,
  componentName: ExperimentalInputsComponentName
): Array<Array<ColumnConstraint>> {
  const delegatedColumnConstraints: Array<Array<ColumnConstraint>> = fillArray(
    experimentalInputs.parameterNames[componentName].length,
    (parameterIndex) =>
      experimentalInputs.fields[componentName][parameterIndex].map((field) => {
        const isMultiValue =
          field.allowMultiValueForPlateMap &&
          experimentalInputs.parameterConfirmationMethods[componentName][parameterIndex] ===
            ProcedureParameterConfirmationMethod.PLATE_MAP;
        const schemaField: SchemaField = {...field.schemaField, isMulti: isMultiValue};
        const selectorsById = {};
        const structuredTableColumn = makeSchemaFieldStructuredColumn(schemaField, selectorsById);
        const columnDefinition = toSchemaFieldColumnDefinition(schemaField, structuredTableColumn);
        return {structuredTableColumn, columnDefinition};
      })
  );
  return delegatedColumnConstraints;
}
